/**
 * JobsPostPage — /jobs/post
 *
 * Single-page 8-step job posting form. Follows the spec exactly:
 * title → category → location → employment type → description →
 * requirements → salary → contact. Individual posters must sign a
 * light consent.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useMyBusinesses } from "@/lib/useMyBusinesses";
import { JOB_CATEGORIES, EMPLOYMENT_TYPES, labelFor } from "@/data/jobCategories";
import { Briefcase, ArrowLeft, Loader2, Send, Building2, User, AlertTriangle } from "lucide-react";

const INDIVIDUAL_CONSENT = (
  "I confirm this is a legitimate opportunity, that I have the right to hire for it, " +
  "and that I will not use Social Pulse Jobs to post spam, discriminatory, illegal, or misleading listings."
);

function Row({ label, hint, required, testid, children }) {
  return (
    <div className="mb-4" data-testid={testid}>
      <div className="text-[11px] font-mono uppercase tracking-wider opacity-70 mb-1">
        {label}{required && " *"}
      </div>
      {children}
      {hint && <div className="text-[10px] opacity-60 mt-1">{hint}</div>}
    </div>
  );
}

export default function JobsPostPage() {
  const { user } = useAuth();
  const { items: myBiz } = useMyBusinesses();
  const nav = useNavigate();

  const [posterType, setPosterType] = useState("individual");
  const [businessId, setBusinessId] = useState("");
  const [form, setForm] = useState({
    title: "", category: "", employment_type: "", description: "", requirements: "",
    benefits: "", city: user?.city || "", state: user?.state || "", country: user?.country || "",
    remote: false, salary: "", contact_via: "socialpulse", contact_value: "",
  });
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // If the user owns a business but hasn't picked a poster yet, pre-fill
    // the business_id (but leave `Individual` selected — user picks
    // deliberately). This avoids surprising folks who wanted to post a
    // personal gig.
    if (myBiz.length > 0 && !businessId) setBusinessId(myBiz[0].id);
  }, [myBiz.length]);  // eslint-disable-line

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const isIndividual = posterType === "individual";

  const canSubmit = useMemo(() => (
    form.title.trim().length >= 3 &&
    form.category &&
    form.employment_type &&
    form.description.trim().length >= 10 &&
    (form.remote || form.city || form.country) &&
    (!isIndividual || consent) &&
    (posterType === "individual" || businessId)
  ), [form, isIndividual, consent, businessId, posterType]);

  const submit = async () => {
    if (!canSubmit) { toast.error("Please complete all required fields."); return; }
    setBusy(true);
    try {
      const payload = {
        ...form, poster_type: posterType,
        business_id: posterType === "individual" ? null : businessId,
        consent_agreed: isIndividual ? consent : null,
      };
      const { data } = await http.post("/jobs", payload);
      toast.success("Job posted.");
      nav(`/jobs/${data.job.slug || data.job.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Post failed.");
    } finally { setBusy(false); }
  };

  if (!user) return (
    <div className="max-w-md mx-auto px-4 py-16 text-center" data-testid="jobs-post-signin">
      <Briefcase className="w-10 h-10 mx-auto opacity-40 mb-2" />
      <h1 className="font-display text-2xl mb-2">Sign in to post a job</h1>
      <Link to="/login?redirect=/jobs/post" className="wofy-btn-primary inline-flex">Sign in</Link>
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto px-4 py-8" data-testid="jobs-post-page">
      <Link to="/jobs" className="text-xs opacity-70 inline-flex items-center gap-1 mb-3">
        <ArrowLeft className="w-3 h-3" /> All jobs
      </Link>
      <h1 className="font-display text-4xl mb-6">Post a job</h1>

      {/* Poster type */}
      <Row label="Post as" required testid="jp-poster-type">
        <div className="flex flex-wrap gap-2">
          {myBiz.length > 0 && myBiz.map((b) => (
            <button
              key={b.id}
              onClick={() => { setPosterType(b.industry === "organizations" || b.industry === "charities" ? "community" : "business"); setBusinessId(b.id); }}
              className="text-sm px-3 py-2 rounded-full border inline-flex items-center gap-1.5"
              style={{
                background: businessId === b.id ? "var(--wofy-primary)" : "transparent",
                color:      businessId === b.id ? "white" : "var(--wofy-text)",
                borderColor: "var(--wofy-border)",
              }}
              data-testid={`jp-poster-biz-${b.slug}`}
            >
              <Building2 className="w-3.5 h-3.5" /> {b.name}
            </button>
          ))}
          <button
            onClick={() => { setPosterType("individual"); setBusinessId(""); }}
            className="text-sm px-3 py-2 rounded-full border inline-flex items-center gap-1.5"
            style={{
              background: isIndividual ? "var(--wofy-primary)" : "transparent",
              color:      isIndividual ? "white" : "var(--wofy-text)",
              borderColor: "var(--wofy-border)",
            }}
            data-testid="jp-poster-individual"
          >
            <User className="w-3.5 h-3.5" /> Individual / gig
          </button>
        </div>
      </Row>

      <Row label="Job title" required testid="jp-title">
        <input value={form.title} onChange={(e) => set("title", e.target.value)} className="wofy-input" placeholder="Part-time receptionist" data-testid="jp-title-input" />
      </Row>

      <Row label="Category" required testid="jp-category">
        <select value={form.category} onChange={(e) => set("category", e.target.value)} className="wofy-input" data-testid="jp-category-select">
          <option value="">— select —</option>
          {Object.entries(JOB_CATEGORIES).map(([gid, g]) => (
            <optgroup key={gid} label={`${g.emoji}  ${g.label}`}>
              {g.subs.map((s) => <option key={`${gid}-${s}`} value={s}>{labelFor(s)}</option>)}
            </optgroup>
          ))}
        </select>
      </Row>

      <div className="grid md:grid-cols-3 gap-3 mb-4">
        <div className="md:col-span-2">
          <Row label="City" testid="jp-city">
            <input value={form.city} onChange={(e) => set("city", e.target.value)} className="wofy-input" data-testid="jp-city-input" />
          </Row>
        </div>
        <Row label="Country" testid="jp-country">
          <input value={form.country} onChange={(e) => set("country", e.target.value)} className="wofy-input" data-testid="jp-country-input" />
        </Row>
      </div>
      <label className="inline-flex items-center gap-2 text-sm mb-4">
        <input type="checkbox" checked={form.remote} onChange={(e) => set("remote", e.target.checked)} data-testid="jp-remote" />
        This is a fully-remote role
      </label>

      <Row label="Employment type" required testid="jp-emp-type">
        <div className="flex flex-wrap gap-2">
          {EMPLOYMENT_TYPES.map((t) => (
            <button
              key={t}
              onClick={() => set("employment_type", t)}
              className="text-sm px-3 py-1.5 rounded-full border"
              style={{
                background: form.employment_type === t ? "var(--wofy-primary)" : "transparent",
                color:      form.employment_type === t ? "white" : "var(--wofy-text)",
                borderColor: "var(--wofy-border)",
              }}
              data-testid={`jp-emp-${t}`}
            >
              {labelFor(t)}
            </button>
          ))}
        </div>
      </Row>

      <Row label="Description" required hint="What's the role? What will the person do day-to-day?" testid="jp-description">
        <textarea rows={6} value={form.description} onChange={(e) => set("description", e.target.value)} className="wofy-input" data-testid="jp-description-input" />
      </Row>
      <Row label="Requirements" hint="Skills, experience, availability, must-haves." testid="jp-requirements">
        <textarea rows={4} value={form.requirements} onChange={(e) => set("requirements", e.target.value)} className="wofy-input" data-testid="jp-requirements-input" />
      </Row>
      <Row label="Benefits (optional)" testid="jp-benefits">
        <textarea rows={3} value={form.benefits} onChange={(e) => set("benefits", e.target.value)} className="wofy-input" data-testid="jp-benefits-input" />
      </Row>

      <Row label="Salary (optional)" hint="Free-text. E.g. $25/hour, $3,000/month, Negotiable, Volunteer." testid="jp-salary">
        <input value={form.salary} onChange={(e) => set("salary", e.target.value)} className="wofy-input" placeholder="$25/hour" data-testid="jp-salary-input" />
      </Row>

      <div className="grid md:grid-cols-3 gap-3 mb-4">
        <Row label="Receive applications via" required testid="jp-contact-via">
          <select value={form.contact_via} onChange={(e) => set("contact_via", e.target.value)} className="wofy-input" data-testid="jp-contact-via-select">
            <option value="socialpulse">Social Pulse (recommended)</option>
            <option value="email">Email</option>
            <option value="phone">Phone</option>
          </select>
        </Row>
        <div className="md:col-span-2">
          <Row label={form.contact_via === "socialpulse" ? "— nothing needed" : "Address or number"} testid="jp-contact-value">
            <input
              value={form.contact_value} onChange={(e) => set("contact_value", e.target.value)}
              className="wofy-input" disabled={form.contact_via === "socialpulse"}
              placeholder={form.contact_via === "email" ? "hr@yourcompany.com" : form.contact_via === "phone" ? "+91 484 555 0100" : ""}
              data-testid="jp-contact-value-input"
            />
          </Row>
        </div>
      </div>

      {isIndividual && (
        <div className="rounded-lg border p-4 space-y-2 bg-amber-50/60 mb-4" style={{ borderColor: "#F59E0B" }} data-testid="jp-consent-block">
          <div className="inline-flex items-center gap-1.5 font-semibold text-sm text-amber-900">
            <AlertTriangle className="w-4 h-4" /> Community consent
          </div>
          <p className="text-xs text-amber-950/90 leading-relaxed">{INDIVIDUAL_CONSENT}</p>
          <label className="inline-flex items-start gap-2 text-xs pt-1">
            <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="jp-consent-checkbox" className="mt-0.5" />
            <span>I confirm the statements above.</span>
          </label>
        </div>
      )}

      <div className="flex items-center justify-end gap-2 pt-4 border-t" style={{ borderColor: "var(--wofy-border)" }}>
        <Link to="/jobs" className="wofy-btn-ghost">Cancel</Link>
        <button onClick={submit} disabled={!canSubmit || busy} className="wofy-btn-primary inline-flex items-center gap-2 disabled:opacity-50" data-testid="jp-publish">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          Publish job
        </button>
      </div>
    </div>
  );
}
